/**
 * Rng — seeded deterministic randomness. One stream per system, forkable by salt,
 * so a run seed reproduces byte-identically on the main thread and inside a Worker.
 */

const U32 = 4294967296;
const TAU = Math.PI * 2;
/** smallest value next() can return above zero; keeps log(0) out of gauss() */
const EPS_UNIT = 1 / U32;

// Bit-level fold for values that do not fit an int32 (Date.now seeds, fractions, NaN).
// Typed-array views are platform-endian, which is stable for any single client.
const _f64 = new Float64Array(1);
const _u32 = new Uint32Array(_f64.buffer);

function foldToU32(v: number): number {
  if (Number.isInteger(v) && v >= -2147483648 && v <= 4294967295) return v >>> 0;
  _f64[0] = v;
  return (_u32[0] ^ Math.imul(_u32[1], 0x9e3779b1)) >>> 0;
}

/** Standalone mulberry32 generator. Same sequence as `Rng`, without the state accessor. */
export function mulberry32(seed: number): () => number {
  let a = foldToU32(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / U32;
  };
}

/** FNV-1a over UTF-16 code units → uint32. Used to seed from human-typed strings. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Murmur3-style mix of an ordered integer tuple → uint32. hashInts(1,2) !== hashInts(2,1). */
export function hashInts(...vals: number[]): number {
  let h = 0x9e3779b1;
  for (let i = 0; i < vals.length; i++) {
    let k = foldToU32(vals[i]);
    k = Math.imul(k, 0xcc9e2d51);
    k = (k << 15) | (k >>> 17);
    k = Math.imul(k, 0x1b873593);
    h ^= k;
    h = (h << 13) | (h >>> 19);
    h = (Math.imul(h, 5) + 0xe6546b64) | 0;
  }
  h ^= vals.length;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

export class Rng {
  /** the seed as constructed — fork() derives from this, never from the live state */
  private readonly root: number;
  private s: number;
  private spare = 0;
  private hasSpare = false;

  constructor(seed: number) {
    this.root = foldToU32(seed);
    this.s = this.root;
  }

  get state(): number {
    return this.s >>> 0;
  }

  next(): number {
    const a = (this.s + 0x6d2b79f5) | 0;
    this.s = a;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / U32;
  }

  // Every public draw consumes exactly one next(), degenerate arguments included —
  // a caller whose range collapses must not desync every later draw in the stream.
  int(minInclusive: number, maxExclusive: number): number {
    const r = this.next();
    const span = maxExclusive - minInclusive;
    if (span <= 0) return minInclusive;
    return minInclusive + Math.floor(r * span);
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  bool(p = 0.5): boolean {
    return this.next() < p;
  }

  pick<T>(arr: readonly T[]): T {
    return arr[this.int(0, arr.length)];
  }

  /** Box–Muller: two uniforms in, two normals out. The second is cached for the next call. */
  gauss(mean = 0, sd = 1): number {
    if (this.hasSpare) {
      this.hasSpare = false;
      return mean + sd * this.spare;
    }
    const u1 = Math.max(this.next(), EPS_UNIT);
    const th = TAU * this.next();
    const r = Math.sqrt(-2 * Math.log(u1));
    this.spare = r * Math.sin(th);
    this.hasSpare = true;
    return mean + sd * r * Math.cos(th);
  }

  /**
   * Independent stream keyed by (root seed, salt). Deliberately does not touch `this`:
   * several systems fork the same parent and must agree no matter which runs first.
   */
  fork(salt: number): Rng {
    return new Rng(hashInts(this.root, salt));
  }

  /** Unit vector on the XZ plane. Matches the +Z-forward heading convention. */
  dir2(): { x: number; z: number } {
    const a = this.next() * TAU;
    return { x: Math.sin(a), z: Math.cos(a) };
  }
}
